import { FilterDropdown } from "@/components/Filter/FilterDropdown";
import { applyFilters, useFilter } from "@/components/Filter/useFilter";
import { Page, PageBody, PageHeader } from "@/components/app-layout";
import { KitDeletionDialog } from "@/components/dependency-deletion/KitDeletionDialog";
import { GroupOrUnknownDisplayName } from "@/components/display/GroupDisplayName";
import { UserOrUnknownDisplayName } from "@/components/display/UserDisplayName";
import { KitDisplayName } from "@/components/display/display-name/KitDisplayName";
import { GrantDialog } from "@/components/grant/GrantDialog";
import { ProjectPresets } from "@/components/preset/ProjectPresets";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Row } from "@/components/ui/flex";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuthDetails } from "@/context/auth-context";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsisV,
  faGear,
  faPen,
  faShareNodes,
  faTrash,
  faUserGroup,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  GroupedTable,
  GroupedTableColumn,
} from "../components/GroupedTable/GroupedTable";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { useKitsData } from "../data/use-kits-data";
import { useUsersData } from "../data/use-users-data";
import { useGroupSlug } from "../hooks/use-group-slug";
import { groupByDate, smartDate } from "../lib/date";
import { GetKitDto } from "../lib/gen-api";

type ProjectScope = "all" | "mine" | "group" | "shared";

export const ProjectsPage = () => {
  const groupUuid = useGroupUuid();
  const groupSlug = useGroupSlug();
  const authDetails = useAuthDetails();
  const [scope, setScope] = useState<ProjectScope>("all");

  const kitsFilter = useMemo(() => {
    const base = { type: "project" as const };

    if (scope === "shared") {
      return { ...base, shared: true as const, owned: false as const };
    }

    if (scope === "mine" || scope === "group") {
      return { ...base, owned: true as const };
    }

    return base;
  }, [scope]);

  const { kits } = useKitsData(groupUuid, kitsFilter);
  const { getUserForUserUuid } = useUsersData(groupUuid);

  const scopedKits = useMemo(() => {
    if (scope === "mine") {
      return kits.filter((kit) => kit.user_uuid === authDetails?.uuid);
    }

    if (scope === "group") {
      return kits.filter((kit) => kit.user_uuid !== authDetails?.uuid);
    }

    return kits;
  }, [authDetails?.uuid, kits, scope]);

  const getOwnerLabel = (userUuid: string | null) => {
    if (!userUuid) return "All Users";
    const kitOwner = scopedKits.find((kit) => kit.user_uuid === userUuid)?.user;
    return (
      kitOwner?.name ?? getUserForUserUuid(userUuid)?.name ?? "Unknown User"
    );
  };

  const {
    selected: selectedUser,
    setSelected: setSelectedUser,
    options: users,
  } = useFilter(scopedKits, "user_uuid");
  const filteredKits = applyFilters(scopedKits, {
    user_uuid: selectedUser,
  });
  const groupedProjects = useMemo(() => {
    return groupByDate(filteredKits, "date_updated");
  }, [filteredKits]);

  const columns = useMemo((): GroupedTableColumn<GetKitDto>[] => {
    const baseColumns: GroupedTableColumn<GetKitDto>[] = [
      { key: "name", label: "Name" },
      { key: "date_updated", label: "Last Modified" },
      { key: "group", label: "Location" },
      { key: "user_uuid", label: "Owned By" },
    ];

    if (scope === "shared") {
      baseColumns.push({
        key: "shared_by_user_uuid",
        label: "Shared By",
      });
    }

    return baseColumns;
  }, [scope]);

  return (
    <Page className="bg-background-1">
      <PageHeader>
        <PageHeader.Title>Workbooks</PageHeader.Title>
      </PageHeader>
      <PageBody innerClassName="gap-8">
        <ProjectPresets />

        <Tabs
          value={scope}
          onValueChange={(value) => setScope(value as ProjectScope)}
          className="w-full"
        >
          <TabsList>
            <TabsTrigger value="all">All Workbooks</TabsTrigger>
            <TabsTrigger value="mine">My Workbooks</TabsTrigger>
            <TabsTrigger value="group">Group Workbooks</TabsTrigger>
            <TabsTrigger value="shared">Shared with Me</TabsTrigger>
          </TabsList>
        </Tabs>

        <Row className="gap-2">
          <FilterDropdown
            label="Owned By"
            options={users}
            value={selectedUser}
            onChange={setSelectedUser}
            getLabel={(user) => (user ? getOwnerLabel(user) : "All Users")}
            allLabel="All Users"
            className="border-border"
          />
        </Row>

        <GroupedTable
          isSortable
          isSearchable
          data={groupedProjects}
          columns={columns}
          itemIdKey="uuid"
          renderCell={{
            name: (_, row) => <ProjectNameCell kit={row} />,
            user_uuid: (_, row) => (
              <UserOrUnknownDisplayName user={row.user ?? null} />
            ),
            shared_by_user_uuid: (_, row) => (
              <UserOrUnknownDisplayName user={row.shared_by_user ?? null} />
            ),
            group: (_, row) => (
              <GroupOrUnknownDisplayName group={row.group ?? null} />
            ),
            date_updated: (value) => smartDate(value),
          }}
          renderActions={({ row }) => <RowActions row={row} />}
          rowLink={(row) => ({
            to: "/w/$groupSlug/k/$kitUuid",
            params: { groupSlug, kitUuid: row.uuid },
          })}
          emptyMessage="Your workbooks will show up here. Create your first to kick things off"
        />
      </PageBody>
    </Page>
  );
};

function ProjectNameCell({ kit }: { kit: GetKitDto }) {
  return (
    <Row className="items-center gap-2">
      <KitDisplayName kit={kit} />
      {kit.is_published && (
        <Badge
          variant="outline"
          className="text-foreground border-border rounded-full px-2 py-0 text-[10px] font-medium"
        >
          public
        </Badge>
      )}
      {kit.is_shared && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-muted-foreground">
                <FontAwesomeIcon icon={faUserGroup} />
              </span>
            </TooltipTrigger>
            <TooltipContent>Shared with other groups</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </Row>
  );
}

function RowActions({ row }: { row: GetKitDto }) {
  const groupUuid = useGroupUuid();
  const groupSlug = useGroupSlug();
  const authDetails = useAuthDetails();
  const { updateKit } = useKitsData(groupUuid);
  const [renameOpen, setRenameOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [newName, setNewName] = useState(row.name);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isHomeGroup = row.group_uuid === groupUuid;
  const isOwner = row.user_uuid === authDetails?.uuid;
  const canRename = isHomeGroup;
  const canShowShare = isHomeGroup && (isOwner || Boolean(row.is_published));
  const canDeleteProject = isHomeGroup && isOwner;

  useEffect(() => {
    setNewName(row.name);
  }, [row.name]);

  const handleRename = async () => {
    setLoading(true);
    setError(null);

    try {
      await updateKit({ kitUuid: row.uuid, name: newName });
      setRenameOpen(false);
      toast.success("Workbook name updated");
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_) {
      setError("Failed to update name");
      toast.error("Failed to update name");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="More actions"
            title="More actions"
          >
            <FontAwesomeIcon icon={faEllipsisV} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canRename && (
            <DropdownMenuItem onClick={() => setRenameOpen(true)}>
              <FontAwesomeIcon icon={faPen} /> Rename
            </DropdownMenuItem>
          )}
          {canShowShare && groupUuid && (
            <DropdownMenuItem onClick={() => setShareOpen(true)}>
              <FontAwesomeIcon icon={faShareNodes} /> Share
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild>
            <Link
              to="/w/$groupSlug/k/$kitUuid/workbook-settings"
              params={{ groupSlug, kitUuid: row.uuid }}
              className="flex flex-row items-center gap-2"
            >
              <FontAwesomeIcon icon={faGear} /> Settings
            </Link>
          </DropdownMenuItem>
          {canDeleteProject && (
            <DropdownMenuItem onClick={() => setDeleteOpen(true)}>
              <FontAwesomeIcon icon={faTrash} /> Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Workbook Name</DialogTitle>
            <DialogDescription>
              Change the name of this workbook. This will be visible to all
              workspace members.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Workbook name"
            disabled={loading}
            className="mb-2"
            autoFocus
          />
          {error && <div className="mb-2 text-sm text-red-500">{error}</div>}
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setRenameOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleRename}
              disabled={loading || !newName.trim()}
            >
              {loading ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {canShowShare && groupUuid && (
        <Dialog open={shareOpen} onOpenChange={setShareOpen}>
          <DialogContent className="sm:max-w-[800px]">
            <DialogHeader>
              <DialogTitle>Share Workbook</DialogTitle>
              <DialogDescription>
                Manage visibility and share {row.name} with other groups.
              </DialogDescription>
            </DialogHeader>
            <GrantDialog
              groupUuid={groupUuid}
              objectName={row.name}
              kitUuid={row.uuid}
            />
          </DialogContent>
        </Dialog>
      )}

      {canDeleteProject && (
        <KitDeletionDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          groupUuid={groupUuid!}
          kitUuid={row.uuid}
        />
      )}
    </>
  );
}
